import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useTranslation } from '../LanguageContext';
import styles from './Nav.module.css';

// Register GSAP plugin
gsap.registerPlugin(ScrollToPlugin);

const Navigation = () => {
  const { t, isEnglish } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navLinksRef = useRef([]);
  const contactInfoRef = useRef(null);
  const footerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    // Initialize GSAP timeline
    timelineRef.current = gsap.timeline({ paused: true });

    const tl = timelineRef.current;

    // Menu slide in
    tl.set(menuRef.current, { display: 'flex' })
      .fromTo(
        menuRef.current,
        { clipPath: isEnglish ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)' },
        {
          clipPath: 'inset(0 0 0 0%)',
          duration: 0.7,
          ease: 'power4.inOut'
        }
      )
      // Close button
      .fromTo(
        closeButtonRef.current,
        { opacity: 0, x: isEnglish ? -20 : 20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          ease: 'power3.out'
        },
        '-=0.3'
      )
      // Stagger nav links
      .fromTo(
        navLinksRef.current,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.08,
          duration: 0.6,
          ease: 'power3.out'
        },
        '-=0.2'
      )
      // Contact info fade in
      .fromTo(
        contactInfoRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.5,
          ease: 'power3.out'
        },
        '-=0.3'
      )
      // Footer
      .fromTo(
        footerRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.4,
          ease: 'power3.out'
        },
        '-=0.2'
      );

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [isEnglish]);

  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
    timelineRef.current.play();
  };

  const closeMenu = () => {
    timelineRef.current.reverse();
    setTimeout(() => {
      setIsOpen(false);
      document.body.style.overflow = '';
    }, 700);
  };

  const scrollToSection = (targetId) => {
    closeMenu();
    setTimeout(() => {
      gsap.to(window, {
        duration: 1,
        scrollTo: {
          y: `#${targetId}`,
          offsetY: 80,
        },
        ease: 'power3.inOut',
      });
    }, 750);
  };

  const handleWhatsApp = () => {
    const phone = '972544998929';
    const message = encodeURIComponent(t.nav.whatsappMessage);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    closeMenu();
  };

  const handleNavClick = (item) => {
    if (item.action === 'whatsapp') {
      handleWhatsApp();
    } else {
      scrollToSection(item.targetId);
    }
  };

  const dirStyle = isEnglish ? { direction: 'ltr' } : { direction: 'rtl' };
  const textAlign = isEnglish ? { textAlign: 'left' } : { textAlign: 'right' };

  return (
    <>
      {/* Fixed Top Bar */}
      <nav className={styles.topBar} style={dirStyle}>
        <button
          className={styles.menuButton}
          onClick={openMenu}
          aria-label={t.nav.openMenu}
        >
          <span className={styles.menuIcon}>
            <span className={styles.menuLine}></span>
            <span className={styles.menuLine}></span>
          </span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Language Switcher */}
          <a
            href={isEnglish ? '/' : '/english'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textDecoration: 'none',
              fontSize: '0.85rem',
              fontFamily: "'HeeboR', 'Heebo', sans-serif",
              color: 'var(--narkis-brown)',
              opacity: 0.8,
              transition: 'opacity 0.3s ease',
              padding: '6px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(47, 26, 18, 0.15)',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
          >
            {isEnglish ? (
              <>
                <span style={{ fontSize: '1.1rem' }}>🇮🇱</span>
                <span>עברית</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: '1.1rem' }}>🇺🇸</span>
                <span>English</span>
              </>
            )}
          </a>

          <a href={isEnglish ? '/english' : '/'} className={styles.logo}>
            <span className={styles.logoText}>NRB</span>
          </a>
        </div>
      </nav>

      {/* Full Screen Menu */}
      <div
        ref={menuRef}
        className={styles.fullscreenMenu}
        style={{ display: isOpen ? 'flex' : 'none', direction: isEnglish ? 'ltr' : 'rtl' }}
      >
        <div className={styles.menuContent}>
          {/* Close Button */}
          <button
            ref={closeButtonRef}
            className={styles.closeButton}
            onClick={closeMenu}
            aria-label={t.nav.closeMenu}
            style={isEnglish ? { right: 'auto', left: '40px' } : {}}
          >
            <span className={styles.closeIcon}>
              <span className={styles.closeLine}></span>
              <span className={styles.closeLine}></span>
            </span>
          </button>

          {/* Navigation Links */}
          <div className={styles.navSection}>
            <nav className={styles.navLinks}>
              {t.nav.items.map((item, index) => (
                <button
                  key={item.number}
                  ref={(el) => (navLinksRef.current[index] = el)}
                  onClick={() => handleNavClick(item)}
                  className={styles.navLink}
                  style={textAlign}
                >
                  <span className={styles.navNumber}>{item.number}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                  <span className={styles.navArrow}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      {isEnglish
                        ? <path d="M5 12H19M12 5l7 7-7 7"/>
                        : <path d="M19 12H5M12 5l-7 7 7 7"/>
                      }
                    </svg>
                  </span>
                </button>
              ))}

              {/* Language Switcher in Menu */}
              <a
                href={isEnglish ? '/' : '/english'}
                className={styles.navLink}
                style={{ ...textAlign, textDecoration: 'none' }}
                onClick={closeMenu}
              >
                <span className={styles.navNumber}>05</span>
                <span className={styles.navLabel}>
                  {isEnglish ? (
                    <>🇮🇱 עברית</>
                  ) : (
                    <>🇺🇸 English</>
                  )}
                </span>
              </a>
            </nav>
          </div>

          {/* Contact Information */}
          <div ref={contactInfoRef} className={styles.contactSection} style={isEnglish ? { paddingRight: 0, borderRight: 'none', paddingLeft: '60px', borderLeft: '1px solid var(--nav-border)' } : {}}>
            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>{t.nav.phone}</span>
              <a href="tel:+972544998929" className={styles.contactValue}>
                054-499-8929
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>{t.nav.email}</span>
              <a href="mailto:nbnarkisbarazani@gmail.com" className={styles.contactValue}>
                nbnarkisbarazani@gmail.com
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>{t.nav.follow}</span>
              <div className={styles.socialLinks}>
                <a
                  href="https://www.instagram.com/narkis_barazani_designer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/Narkis.Barazani.Interior.Designer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  Facebook
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div ref={footerRef} className={styles.menuFooter}>
            <span className={styles.footerText}>Narkis Rubin Barazani</span>
            <span className={styles.footerDivider}></span>
            <span className={styles.footerText}>{t.nav.footerTagline}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
